import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import { isCoachSelectionTestVariant } from 'nove8/cohorts/woofz_woofy1/abtest/isCoachSelectionTestVariant'
import { getSelectedAnswers } from 'nove8/helpers/getSelectedAnswers'
import { TStep } from 'nove8/types'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isChatUrlRestoreBlocked = (): boolean => {
  const savedAnswers =
    useZustandSessionStore.getState().userAnswers ||
    useZustandGlobalStore.getState().lead?.quizzes ||
    null

  if (!savedAnswers) return true

  if (isCoachSelectionTestVariant()) {
    return isEmpty(getSelectedAnswers(savedAnswers, PageId.COACH_WOOFY))
  }

  return [
    PageId.HELLO_WOOFY,
    PageId.DOG_AGE_WOOFY,
    PageId.BEHAVIOR_WOOFY,
  ].some((pageId) => isEmpty(getSelectedAnswers(savedAnswers, pageId)))
}

export const getXStateConfigWoofzWoofy = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''

            // A/B share /chat URL + same lead uuid; skip restore when active variant's quiz is missing.
            if (
              pageIdFromUrl === PageId.CHAT_WOOFY &&
              isChatUrlRestoreBlocked()
            ) {
              return false
            }

            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_WOOFY,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_WOOFY,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_WOOFY,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.CHAT_WOOFY && {
            NEXT_FLOW_STEP: {
              target: pages[index + 1]?.pageId,
            },
            SKIP_ONE_STEP: {
              target: PageId.EMAIL_WOOFY,
            },
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_WOOFY]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_WOOFY,
    },
  },
  [PageId.DOWNLOAD_WOOFY]: {
    type: 'final',
  },
})
